
import { useState, useCallback, useEffect } from 'react';

export interface ProfileFilters {
  // Básico
  gender?: string[];
  orientation?: string[];
  minAge?: number;
  maxAge?: number;
  role?: string[];
  
  // Localização
  cities?: string[];
  states?: string[];
  maxDistance?: number;
  
  // Profissional
  occupations?: string[];
  educations?: string[];
  incomeRanges?: string[];
  
  // Relacionamento
  relationshipTypes?: string[];
  estadoCivil?: string[];
  exclusividade?: string[];
  sigilo?: string[];
  
  // Físico
  alturas?: string[];
  tiposCorpo?: string[];
  coresOlhos?: string[];
  coresCabelo?: string[];
  
  // Estilo
  fumante?: string[];
  bebidas?: string[];
  tatuagens?: string[];
  piercings?: string[];
  estilosVida?: string[];
  idiomas?: string[];
  
  // Interesses
  interests?: string[];
  
  // Verificação
  verificationLevels?: string[];
}

const STORAGE_KEY = 'profile-filters';

export const useProfileFilters = () => {
  const [filters, setFilters] = useState<ProfileFilters>({
    minAge: 18,
    maxAge: 65
  });
  const [isOpen, setIsOpen] = useState(false);

  // Load filters from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsedFilters = JSON.parse(saved);
        setFilters({
          minAge: 18,
          maxAge: 65,
          ...parsedFilters
        });
      } catch {
        // Clear corrupted data to prevent repeated JSON parse errors
        localStorage.removeItem(STORAGE_KEY);
        console.warn('Profile filters in storage were invalid JSON and were reset.');
      }
    }
  }, []);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const updateFilter = useCallback((key: keyof ProfileFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      minAge: 18,
      maxAge: 65
    });
  }, []);

  const getActiveFiltersCount = useCallback(() => {
    return Object.entries(filters).reduce((count, [key, value]) => {
      // Don't count default age values as active filters
      if (key === 'minAge' && value === 18) return count;
      if (key === 'maxAge' && value === 65) return count;
      
      if (Array.isArray(value) && value.length > 0) return count + 1;
      if (typeof value === 'number' && value > 0) return count + 1;
      return count;
    }, 0);
  }, [filters]);

  return {
    filters,
    isOpen,
    setIsOpen,
    updateFilter,
    clearFilters,
    getActiveFiltersCount
  };
};
